import { Link } from "@tanstack/react-router";
import { useEffect } from "react";

import { reportHandledSync } from "@/lib/observability";

import { SiteFooter } from "@/components/site/site-footer";
import { SiteHeader } from "@/components/site/site-header";

/**
 * Branded failure page for public routes whose loaders hit the database.
 * Without it a backend hiccup drops visitors on the router's bare default
 * boundary, which looks like a broken site on the busiest pages we have.
 */
export function PublicErrorState({
  title = "Something went sideways",
  message = "Refresh the page and this should come right back. If it keeps happening, reach out and we will sort it.",
  error,
  route,
}: {
  title?: string;
  message?: string;
  error?: unknown;
  route?: string;
}) {
  useEffect(() => {
    if (!error) return;
    reportHandledSync(error, {
      feature: "public_route",
      action: "route_error_boundary",
      route: route ?? (typeof window !== "undefined" ? window.location.pathname : "unknown"),
    });
  }, [error, route]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="app-light min-h-[60vh]">
        <main
          className="mx-auto max-w-3xl px-4 pb-24 text-center sm:px-6"
          style={{ paddingTop: "calc(var(--site-hdr, 72px) + 5rem)" }}
        >
          <h1 className="t-h1 text-ice">{title}</h1>
          <p className="t-body mt-4">{message}</p>
          <Link to="/" className="mt-8 inline-flex text-sm font-bold uppercase text-cyan">
            Back to the volunteer home
          </Link>
        </main>
      </div>
      <SiteFooter />
    </div>
  );
}
